/**
 * Model Settings Interfaces for OCR Processing Configuration
 */

export type ModelType = 'GPT_4O' | 'GLM_45V' | 'HUGGINGFACE_GLM_45V' | 'GROQ_SCOUT' | 'GROQ_MAVERICK' | 'FIREWORKS_GLM_45V' | 'QWEN2P5_VL_32B_INSTRUCT';

/**
 * SPR OCR Mode: Models available for SPR OCR environment
 * These models use specialized SPR-specific prompts and return JSON output
 */
export const SPR_OCR_MODELS: ModelType[] = [
  'GLM_45V',           // GLM-4.5V/4.6V SPR Service
  'QWEN2P5_VL_32B_INSTRUCT',  // Fireworks Qwen3-VL SPR Service
  'GROQ_SCOUT'         // Groq Llama 4 Scout SPR Service
];

/**
 * Check if a model is available in SPR OCR mode
 */
export function isSprOcrModel(modelType: ModelType): boolean {
  return SPR_OCR_MODELS.includes(modelType);
}

export interface GPT4OSettings {
  processingMode: 'json' | 'image';
  optimizeImageSize: boolean;
  useImageUrl: boolean;
}

export interface GLM45VSettings {
  thinkingMode: boolean;
  optimizeImageSize: boolean;
  useImageUrl: boolean;
  apiTimeout: number;
  glmModelVersion: 'glm-4.5v' | 'glm-4.6v';
}

export interface HuggingFaceGLMSettings {
  thinkingMode: boolean;
  optimizeImageSize: boolean;
  useImageUrl: boolean;
  apiTimeout: number;
}

export interface GroqSettings {
  optimizeImageSize: boolean;
  useImageUrl: boolean;
  apiTimeout: number;
}

export interface FireworksGLMSettings {
  thinkingMode: boolean;
  optimizeImageSize: boolean;
  useImageUrl: boolean;
  apiTimeout: number;
}

export interface Qwen2p5VLSettings {
  thinkingMode: boolean;
  optimizeImageSize: boolean;
  useImageUrl: boolean;
  apiTimeout: number;
}

export type ModelSettings = GPT4OSettings | GLM45VSettings | HuggingFaceGLMSettings | GroqSettings | FireworksGLMSettings | Qwen2p5VLSettings;

export interface ModelConfiguration {
  enabled: boolean;
  name: string;
  settings: ModelSettings;
  isDeprecated?: boolean; // API no longer supported - model cannot be used
}

export interface ModelSettingsConfig {
  primaryModel: ModelType;
  modelOrder: ModelType[];
  useTraditionalPipelineOnly: boolean; // New option to disable all AI models
  models: {
    [K in ModelType]: ModelConfiguration;
  };
}

export interface UserModelSettings {
  id: string;
  userId?: string;
  settings: ModelSettingsConfig;
  createdAt: Date;
  updatedAt: Date;
}

export interface ModelSettingsResponse {
  success: boolean;
  settings?: ModelSettingsConfig;
  message?: string;
  useEnvironmentSettings: boolean;
}

export interface ModelValidationResult {
  isValid: boolean;
  errors: string[];
  warnings: string[];
}

export interface ModelCapabilities {
  supportedFormats: string[];
  maxImageSize: number;
  supportsThinking: boolean;
  supportsImageUrl: boolean;
  apiTimeoutRange: { min: number; max: number };
}

export const MODEL_CAPABILITIES: Record<ModelType, ModelCapabilities> = {
  'GPT_4O': {
    supportedFormats: ['jpg', 'jpeg', 'png', 'webp'],
    maxImageSize: 20 * 1024 * 1024, // 20MB
    supportsThinking: false,
    supportsImageUrl: true,
    apiTimeoutRange: { min: 30, max: 600 }
  },
  'GLM_45V': {
    supportedFormats: ['jpg', 'jpeg', 'png'],
    maxImageSize: 20 * 1024 * 1024, // 20MB
    supportsThinking: true,
    supportsImageUrl: true,
    apiTimeoutRange: { min: 60, max: 600 }
  },
  'HUGGINGFACE_GLM_45V': {
    supportedFormats: ['jpg', 'jpeg', 'png'],
    maxImageSize: 20 * 1024 * 1024, // 20MB
    supportsThinking: true,
    supportsImageUrl: true,
    apiTimeoutRange: { min: 60, max: 600 }
  },
  'GROQ_SCOUT': {
    supportedFormats: ['jpg', 'jpeg', 'png'],
    maxImageSize: 20 * 1024 * 1024, // 20MB
    supportsThinking: false,
    supportsImageUrl: true,
    apiTimeoutRange: { min: 30, max: 600 }
  },
  'GROQ_MAVERICK': {
    supportedFormats: ['jpg', 'jpeg', 'png'],
    maxImageSize: 20 * 1024 * 1024, // 20MB
    supportsThinking: false,
    supportsImageUrl: true,
    apiTimeoutRange: { min: 30, max: 600 }
  },
  'FIREWORKS_GLM_45V': {
    supportedFormats: ['jpg', 'jpeg', 'png'],
    maxImageSize: 20 * 1024 * 1024, // 20MB
    supportsThinking: true,
    supportsImageUrl: true,
    apiTimeoutRange: { min: 60, max: 600 }
  },
  'QWEN2P5_VL_32B_INSTRUCT': {
    supportedFormats: ['jpg', 'jpeg', 'png'],
    maxImageSize: 20 * 1024 * 1024, // 20MB
    supportsThinking: true,
    supportsImageUrl: true,
    apiTimeoutRange: { min: 60, max: 600 }
  }
};

export const MODEL_DISPLAY_INFO: Record<ModelType, { 
  displayName: string; 
  description: string; 
  provider: string;
  costTier: 'low' | 'medium' | 'high';
  speedTier: 'fast' | 'medium' | 'slow';
  isDeprecated?: boolean; // API no longer supported
  deprecationReason?: string;
}> = {
  'GPT_4O': {
    displayName: 'GPT-4o',
    description: 'OpenAI\'s flagship vision model',
    provider: 'OpenAI',
    costTier: 'high',
    speedTier: 'medium',
    isDeprecated: true,
    deprecationReason: 'API no longer supported'
  },
  'GLM_45V': {
    displayName: 'GLM-Vision',
    description: 'Zhipu AI\'s advanced vision model',
    provider: 'Zhipu AI',
    costTier: 'medium',
    speedTier: 'medium'
  },
  'HUGGINGFACE_GLM_45V': {
    displayName: 'HuggingFace GLM-Vision',
    description: 'GLM-Vision via HuggingFace',
    provider: 'HuggingFace',
    costTier: 'low',
    speedTier: 'slow',
    isDeprecated: true,
    deprecationReason: 'API no longer supported'
  },
  'GROQ_SCOUT': {
    displayName: 'Groq Llama 4 Scout',
    description: 'Fast inference with Llama 4 Scout',
    provider: 'Groq',
    costTier: 'low',
    speedTier: 'fast'
  },
  'GROQ_MAVERICK': {
    displayName: 'Groq Llama 4 Maverick',
    description: 'High-quality inference with Llama 4 Maverick',
    provider: 'Groq',
    costTier: 'medium',
    speedTier: 'fast',
    isDeprecated: true,
    deprecationReason: 'API no longer supported'
  },
  'FIREWORKS_GLM_45V': {
    displayName: 'Fireworks GLM-Vision',
    description: 'GLM-Vision via Fireworks AI platform',
    provider: 'Fireworks AI',
    costTier: 'medium',
    speedTier: 'fast',
    isDeprecated: true,
    deprecationReason: 'API no longer supported'
  },
  'QWEN2P5_VL_32B_INSTRUCT': {
    displayName: 'Fireworks Qwen3-VL-32B',
    description: 'Alibaba\'s Qwen3-VL-32B-Instruct via Fireworks AI',
    provider: 'Fireworks AI',
    costTier: 'medium',
    speedTier: 'fast'
  }
};